
import Vue from 'vue'
import { Component, Prop } from 'vue-property-decorator'
import { Action, Getter } from 's-vuex-class'
import Dropdown from './Dropdown.vue'
import BaseButton from './BaseButton.vue'
import {
    Option,
    FilterRuleOperator,
    getGeneralMultiSelectFilters,
    nonNumericFilters,
} from '@/entities'
import FilterModalMobileHeader from '@/components/FilterModalMobileHeader.vue'
import SearchBox from '@/components/SearchBox.vue'
import { Permissions } from '@/mobile/src/types/auth'
import { getRuleSubOptions } from '@/api/filter'
import InfiniteScroll from '@/mobile/src/components/appV4/InfiniteScroll.vue'
import LoadingCard from '@/mobile/src/components/appV4/LoadingCard.vue'

type OptionSetKey = 'general' | 'custom'

@Component<FilterBarNPSType>({
    components: {
        FilterModalMobileHeader,
        BaseButton,
        Dropdown,
        SearchBox,
        InfiniteScroll,
        LoadingCard,
    },
})
export default class FilterBarNPSType extends Vue {
    @Prop({ type: Array, default: () => [] }) public permissions!: string[]

    @Getter public filterActive
    @Getter public filterRuleOptions
    @Getter public filterLoadingRuleSubOption
    @Getter public filterActiveRuleSubOption
    @Getter public filterActiveRuleSubOptions
    @Getter public filterSearchTerm
    @Getter public filterPatching
    @Getter public tsMobileView
    @Getter hasNetworkConnection!: boolean

    @Action public getRuleOptions
    @Action public loadRuleSubOptions
    @Action public reloadRuleSubOptions
    @Action public clearRuleSubOption
    @Action public setFilterRule
    @Action public removeFilterRule
    @Action public setSearchTerm
    @Action public setUsePagination

    public selectedSubOptions: Array<Option<string>> = []
    public mode: 'single' | 'multi' = 'single'
    public showFilterRuleOperatorOptions = false
    public subOption?: string = ''
    public optionSetKey?: OptionSetKey
    public filterSubOptionValue = ''
    public delayTimer // a delaytime to delay search ajax request
    public infiniteContent: any = []
    public infiniteIsLoading = false
    public infiniteOffset = 1
    public infinitePerPage = 1000
    public infiniteReachedTheEnd = false
    public infiniteSearchTerm = ''

    public hashtagOperators: FilterRuleOperator[] = [
        {
            name: 'has hashtags',
            operator: 'has_hashtags',
        },
        {
            name: 'none',
            operator: 'has_no_hashtags',
        },
    ]

    public get specialHashtagValues() {
        return this.hashtagOperators.map((obj) => obj.operator)
    }

    public strOperators: FilterRuleOperator[] = [
        {
            name: 'is',
            operator: 'in',
        },
        {
            name: 'is not',
            operator: 'notin',
        },
    ]

    public strOperatorExclude = [
        'comments',
        'notes',
        'messaged',
        'published',
        'case',
    ]

    // numeric operators
    public operators: FilterRuleOperator[] = [
        {
            name: 'is  ',
            operator: 'in',
        },
        {
            name: 'is not  ',
            operator: 'notin',
        },
        {
            name: 'Greater than  ',
            operator: 'gt',
        },
        {
            name: 'Less than  ',
            operator: 'lt',
        },
    ]

    public filterRuleOperator: FilterRuleOperator = this.operators[0]

    public async loadMoreContent() {
        this.infiniteIsLoading = true

        if (this.filterActiveRuleSubOption) {
            const { data } = await getRuleSubOptions(
                this.filterActiveRuleSubOption,
                this.infiniteSearchTerm,
                true,
                this.infiniteOffset
            )
            this.updateInfiniteData(data)
        }

        this.infiniteIsLoading = false
        return true
    }

    public updateInfiniteData(data: any[], reset = false, fromSearch = false) {
        if (reset) {
            this.infiniteContent = []
            this.infiniteOffset = 1
            this.infiniteReachedTheEnd = false

            if (!fromSearch) {
                this.infiniteSearchTerm = ''
            }
        } else {
            this.infiniteContent = [...this.infiniteContent, ...data]
            this.infiniteOffset++
            if (
                data.length === 0 ||
                data.length < this.infinitePerPage ||
                data.length > this.infinitePerPage
            ) {
                this.infiniteReachedTheEnd = true
            }
        }
    }

    public get showSubOptions(): boolean {
        const { filterLoadingRuleSubOption, filterActiveRuleSubOption } = this
        return !filterLoadingRuleSubOption && filterActiveRuleSubOption
    }

    public get applyingLabel() {
        return this.filterPatching ? 'Applying...' : 'Apply'
    }

    public get filterIsNumeric() {
        if (nonNumericFilters.includes(this.subOption || '')) {
            return false
        }

        return (
            this.filterActiveRuleSubOptions &&
            this.filterActiveRuleSubOptions.some(
                (element) => !isNaN(element.value)
            )
        )
    }

    public get currentOperators(): FilterRuleOperator[] {
        return this.filterIsNumeric ? this.operators : this.strOperators
    }

    public get hasFilterChanged() {
        const activeRule = this.filterActive.filter_rules.find(
            (rule) => rule.column === this.subOption
        )
        const activeValues = activeRule?.value ?? []
        const selectedValues = this.selectedSubOptions.map(
            (option) => option.value
        )
        const valuesChanged =
            selectedValues.length !== activeValues.length ||
            selectedValues.filter((value) => !activeValues.includes(value))
                .length > 0 ||
            activeValues.filter((value) => !selectedValues.includes(value))
                .length > 0 ||
            (activeRule &&
                activeRule.operator !== this.filterRuleOperator.operator)

        return (
            this.filterPatching ||
            (this.filterRuleOperator.operator !== 'in' &&
                this.filterRuleOperator.operator !== 'notin' &&
                !this.filterSubOptionValue) ||
            (!valuesChanged &&
                (this.filterRuleOperator.operator === 'in' ||
                    this.filterRuleOperator.operator === 'notin'))
        )
    }

    public get activeRuleOptionLabel() {
        if (
            !this.filterActiveRuleSubOption ||
            !this.$store.state.filter.ruleOptions ||
            this.$store.state.filter.ruleOptions.length === 0
        ) {
            return ''
        }

        const activeRuleOption = this.$store.state.filter.ruleOptions.find(
            (ruleOption) => ruleOption.value === this.filterActiveRuleSubOption
        )

        return activeRuleOption?.label ?? ''
    }

    public searchedResults(slice: Boolean = true) {
        const filtered =
            this.filterSearchTerm.length > 0
                ? this.filterActiveRuleSubOptions.filter(
                      (opt) =>
                          String(opt.label)
                              .toLowerCase()
                              .trim()
                              .indexOf(this.filterSearchTerm) > -1
                  )
                : this.filterActiveRuleSubOptions

        if (slice) {
            return filtered.slice(0, 100)
        }

        return filtered
    }

    public mounted() {
        this.setUsePagination(true)
        this.getRuleOptions()
        if (this.filterActiveRuleSubOption) {
            this.loadRuleSubOptions(this.filterActiveRuleSubOption)
        }
    }

    public async initSubOptions(subOption: string, optionSetKey: OptionSetKey) {
        this.updateInfiniteData([], true)
        this.subOption = subOption
        this.optionSetKey = optionSetKey
        // custom fields and some general fields (rating, tag & messaged) allow multiple values
        this.mode =
            optionSetKey === 'general' &&
            !getGeneralMultiSelectFilters().includes(subOption)
                ? 'single'
                : 'multi'

        await this.loadRuleSubOptions(subOption)
        this.updateInfiniteData(this.filterActiveRuleSubOptions)

        // Check for operator in existing rule if non found fallback to first operator
        this.filterRuleOperator =
            this.filterActive.filter_rules.find(
                (rule) => rule.column === this.subOption
            ) ?? this.currentOperators[0]

        await this.refreshSubOptions()
        const optionSet = document.getElementById('sub-options')
        if (optionSet !== null) {
            optionSet.scrollIntoView()
        }
    }

    public refreshSubOptions() {
        const activeRule = this.filterActive.filter_rules.find(
            (rule) =>
                rule.column === this.subOption &&
                rule.operator === this.filterRuleOperator.operator
        )

        if (activeRule) {
            this.filterRuleOperator =
                this.currentOperators.find(
                    (o: FilterRuleOperator) =>
                        o.operator === activeRule.operator
                ) || this.filterRuleOperator
        }

        if (activeRule && this.mode === 'multi') {
            this.selectedSubOptions = activeRule.value
                .map((value: string) => {
                    // Don't show the special hashtag operators as pill options
                    if (this.specialHashtagValues.includes(value)) {
                        return
                    }

                    return {
                        value,
                        label: value,
                    }
                })
                .filter((value?: string) => value !== undefined)
        }

        if (this.filterIsNumeric && !activeRule) {
            this.selectedSubOptions = []
        }

        this.filterSubOptionValue = this.getSelectedSubOptionValue()
    }

    public clearSelectedSubOptions(): void {
        this.selectedSubOptions = []
        this.clearRuleSubOption()
        this.showFilterRuleOperatorOptions = false
    }

    public getSelectedSubOptions() {
        return this.selectedSubOptions
    }

    public getOptionValueLabel(opt) {
        const found = this.filterActiveRuleSubOptions.find(
            (ruleOption) => ruleOption.value === opt.value
        )
        return found ? found.label : opt.label
    }

    public getSelectedSubOptionValue() {
        const option = this.getSelectedSubOptions()[0]
        return (option && option.value) || ''
    }

    public addToSelectedSubOptions(selectedSubOption: Option<string>): void {
        if (
            !this.selectedSubOptions.some(
                (element) => element.value === selectedSubOption.value
            )
        ) {
            this.selectedSubOptions.push(selectedSubOption)
        }
    }

    public removeFromSelectedSubOptions(
        subOptionToRemove: Option<string>
    ): void {
        this.selectedSubOptions = this.selectedSubOptions.filter(
            (element) => element.value !== subOptionToRemove.value
        )
    }

    public handleFilterRuleOperatorOptions(): void {
        this.showFilterRuleOperatorOptions = !this.showFilterRuleOperatorOptions
    }

    public async handleHashTagClickOption(option: FilterRuleOperator) {
        this.mode = 'single'
        let payload = {
            label: this.hashtagOperators[0].name,
            value: this.hashtagOperators[0].operator,
        }
        if (option.operator === this.hashtagOperators[1].operator) {
            // Has NO Hashtags
            payload = {
                label: this.hashtagOperators[1].name,
                value: this.hashtagOperators[1].operator,
            }
        }
        await this.handleClickOption(payload)
        this.showFilterRuleOperatorOptions = false
    }

    public handleSelectOperator(operator: FilterRuleOperator): void {
        this.filterRuleOperator = operator
        this.showFilterRuleOperatorOptions = false
        this.refreshSubOptions()
    }

    public async handleClickOption(option: Option<string>) {
        if (
            !isNaN(option.value as any) &&
            this.filterRuleOperator.operator !== 'in' &&
            this.filterRuleOperator.operator !== 'notin'
        ) {
            this.mode = 'single'
        }
        if (this.mode === 'multi') {
            this.addToSelectedSubOptions({
                value: option.value,
                label: option.label,
            })
        } else {
            await this.setFilterRule({
                id: this.filterActive.id,
                rule: {
                    column: this.filterActiveRuleSubOption,
                    operator: this.filterRuleOperator.operator,
                    value: [option.value],
                },
            })
            setTimeout(
                () => ((this.$refs.dropdown as any).$data.opened = false),
                0
            )
        }
    }

    private closeModal() {
        (this.$refs.dropdown as any).$data.opened = false
    }

    public async saveMultiFilter() {
        if (
            this.filterRuleOperator.operator !== 'in' &&
            this.filterRuleOperator.operator !== 'notin' &&
            !this.filterSubOptionValue
        ) {
            return
        }

        const selectedValues = this.selectedSubOptions.map(({ value }) => value)
        if (
            selectedValues.length === 0 &&
            (this.filterRuleOperator.operator === 'in' ||
                this.filterRuleOperator.operator === 'notin')
        ) {
            // If all values are removed, filter rule need to be remove
            await this.removeFilterRule({
                id: this.filterActive.id,
                column: this.filterActiveRuleSubOption,
                operator: this.filterRuleOperator.operator,
            })
        } else {
            await this.setFilterRule({
                id: this.filterActive.id,
                rule: {
                    column: this.filterActiveRuleSubOption,
                    operator: this.filterRuleOperator.operator,
                    value:
                        this.filterRuleOperator.operator === 'in' ||
                        this.filterRuleOperator.operator === 'notin'
                            ? selectedValues
                            : [this.filterSubOptionValue],
                },
            })
        }
        setTimeout(() => ((this.$refs.dropdown as any).$data.opened = false), 0)

        this.clearSelectedSubOptions()
    }

    public async onSearchInput(value) {
        this.infiniteSearchTerm = value
        clearTimeout(this.delayTimer)
        this.delayTimer = setTimeout(async () => {
            this.updateInfiniteData([], true, true)
            await this.loadMoreContent()
        }, 800)
    }

    public get checkFilterRuleOptions() {
        const filterRuleOptions = JSON.parse(
            JSON.stringify(this.filterRuleOptions)
        )
        // Hide CUSTOM filters
        if (
            filterRuleOptions?.custom &&
            !this.permissions.includes(Permissions.RoleViewNameEmailCustomData)
        ) {
            delete filterRuleOptions?.custom
        }
        return filterRuleOptions
    }
}
